import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useState } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, CheckCheck, Undo2, ScrollText } from "lucide-react";
import { adminListNotificationBulkAudit } from "@/lib/notifications.functions";

export const Route = createFileRoute("/_authenticated/admin/notifications-audit")({
  head: () => ({
    meta: [
      { title: "Notification bulk-action audit — Peopels admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: NotificationsAuditPage,
});

type AuditRow = {
  id: string;
  actor_id: string | null;
  actor_name: string | null;
  action: "notifications.bulk_mark_read" | "notifications.bulk_mark_read_undo" | string;
  notes: string | null;
  after_data: { count?: number; ids?: string[]; scope?: string; type?: string | null } | null;
  created_at: string;
  ip_address: string | null;
  user_agent: string | null;
};

const PAGE_SIZE = 50;

function fmt(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`;
}

function NotificationsAuditPage() {
  const list = useServerFn(adminListNotificationBulkAudit);
  const [action, setAction] = useState<"all" | "notifications.bulk_mark_read" | "notifications.bulk_mark_read_undo">("all");
  const [actorId, setActorId] = useState("");
  const [page, setPage] = useState(0);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["notif-bulk-audit", action, actorId, page],
    queryFn: () => list({ data: { action, actorId: actorId || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE } }),
    placeholderData: keepPreviousData,
  });

  const items = (data?.items ?? []) as AuditRow[];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-5xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link to="/admin/kyc" className="text-sm text-muted-foreground hover:text-foreground">← Back to admin</Link>
            <h1 className="mt-1 flex items-center gap-2 text-xl font-semibold">
              <ScrollText className="h-5 w-5" /> Notification bulk-action audit
            </h1>
            <p className="text-sm text-muted-foreground">
              Every bulk &quot;mark as read&quot; and undo recorded with actor, count, and scope.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}
            Refresh
          </Button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <select
            value={action}
            onChange={(e) => { setPage(0); setAction(e.target.value as typeof action); }}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="all">All actions</option>
            <option value="notifications.bulk_mark_read">Mark read</option>
            <option value="notifications.bulk_mark_read_undo">Undo mark read</option>
          </select>
          <input
            value={actorId}
            onChange={(e) => { setPage(0); setActorId(e.target.value.trim()); }}
            placeholder="Actor user id (uuid)"
            className="h-9 w-72 rounded-md border border-input bg-background px-2 text-sm font-mono"
          />
        </div>

        {isLoading ? (
          <div className="py-16 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border bg-white p-10 text-center text-sm text-muted-foreground">
            No bulk actions match the current filter.
          </div>
        ) : (
          <ol className="space-y-3">
            {items.map((r) => {
              const isUndo = r.action.endsWith("_undo");
              const count = r.after_data?.count ?? 0;
              const scope = r.after_data?.scope ?? "—";
              const type = r.after_data?.type ?? null;
              return (
                <li key={r.id} className="flex gap-3 rounded-2xl border bg-white p-4 shadow-sm">
                  <div className={`mt-0.5 rounded-full p-2 ${isUndo ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {isUndo ? <Undo2 className="h-4 w-4" /> : <CheckCheck className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-sm font-semibold">
                        {isUndo ? "Undid mark-read" : "Marked as read"} · {count} notification{count === 1 ? "" : "s"}
                      </p>
                      <span className="text-xs text-muted-foreground">{fmt(r.created_at)}</span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Actor: {r.actor_name ?? "—"}
                      {r.actor_id ? <span className="ml-1 font-mono">({r.actor_id.slice(0, 8)}…)</span> : null}
                      {" · "} Scope: <span className="font-medium">{scope}</span>
                      {type ? <> · Type: <span className="font-medium">{type}</span></> : null}
                    </p>
                    {r.notes ? <p className="mt-1 text-xs text-muted-foreground">{r.notes}</p> : null}
                    {r.after_data?.ids?.length ? (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs text-sky-700 hover:underline">
                          View {r.after_data.ids.length} notification id{r.after_data.ids.length === 1 ? "" : "s"}
                        </summary>
                        <ul className="mt-2 max-h-32 overflow-auto rounded bg-slate-50 p-2 font-mono text-[10px]">
                          {r.after_data.ids.map((id) => <li key={id}>{id}</li>)}
                        </ul>
                      </details>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        {totalPages > 1 ? (
          <div className="mt-6 flex items-center justify-between gap-3">
            <Button variant="outline" size="sm" disabled={page === 0 || isFetching} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
            <span className="text-xs text-muted-foreground">Page {page + 1} of {totalPages} · {total} entries</span>
            <Button variant="outline" size="sm" disabled={page + 1 >= totalPages || isFetching} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}
